import { Visibility, VisibilityOff } from '@mui/icons-material';
import { IconButton, InputAdornment, TextField, TextFieldProps, useTheme } from '@mui/material';
import { useState } from 'react';
import { Control, Controller, FieldError, FieldPath, FieldValues } from 'react-hook-form';
import { useTranslation } from '../../hooks/useTranslation';

type TextFieldPropsWithoutNameAndError = Omit<TextFieldProps, 'name' | 'error'>;

interface FormTextFieldProps<
    TFormValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
> extends TextFieldPropsWithoutNameAndError {
    name: TName;
    control: Control<TFormValues>;
    error?: FieldError;
    showPasswordToggle?: boolean;
    showErrorsOnlyWhenTouched?: boolean;
}

function FormTextField<
    TFormValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
>({
    name,
    control,
    error,
    showPasswordToggle = false,
    showErrorsOnlyWhenTouched = true,
    ...rest
}: FormTextFieldProps<TFormValues, TName>) {
    const { t } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);
    const theme = useTheme();

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const textFieldStyles = {
        width: '100%',
        marginBottom: '20px',
        '& .MuiInputLabel-root': {
            color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
            fontSize: '1rem',
            fontWeight: 500,
        },
        '& .MuiOutlinedInput-root': {
            height: 56,
            fontSize: '1rem',
            borderRadius: 1,
            boxShadow: theme.palette.mode === 'dark' ? 'none' : 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
            border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
            '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
                borderWidth: 2,
            },
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
            borderWidth: '1px',
        },
        '& .MuiInputBase-input': {
            color: theme.palette.text.primary,
            padding: '10px 14px',
            '&::placeholder': {
                color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                opacity: 1,
                fontWeight: 600,
            },
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
        },
        ...(rest.sx || {}),
    };

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState }) => {
                const shouldShowError = !showErrorsOnlyWhenTouched || fieldState.isTouched;
                const hasError = Boolean(fieldState.error) && shouldShowError;

                return (
                    <TextField
                        {...field}
                        {...rest}
                        type={showPasswordToggle ? (showPassword ? 'text' : 'password') : rest.type}
                        error={hasError}
                        helperText={hasError && typeof fieldState.error?.message === 'string' ? t(fieldState.error.message) : rest.helperText}
                        sx={textFieldStyles}
                        InputProps={{
                            ...rest.InputProps,
                            ...(showPasswordToggle && {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={showPassword ? t('common.form.hidePassword') : t('common.form.showPassword')}
                                            onClick={handleClickShowPassword}
                                            edge="end"
                                            data-testid={`password-visibility-toggle-${name}`}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }),
                        }}
                        inputProps={{
                            ...rest.inputProps,
                            'aria-invalid': hasError,
                            'aria-describedby': hasError ? `${name}-error` : undefined,
                        }}
                        InputLabelProps={{
                            style: {
                                fontWeight: 700,
                                color: theme.palette.text.primary,
                            },
                        }}
                    />
                );
            }}
        />
    );
}

export default FormTextField;
